import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../Redux/Store';
import { CHECKOUT_REQUEST_SESSION_NOT_FOUND, fetchCheckoutRequestState } from '../Redux/Public/orderApi';
import { CheckoutRequestState } from '../types/order';

interface UseCheckoutRequestStateProps {
  activeStoreId: string;
  activeDiningTableId: string;
}

/** REQUESTED / ACKNOWLEDGED 中のポーリング間隔（ms）。設計書 6 章「10 秒間隔」。 */
const POLL_INTERVAL_MS = 10_000;

/** 旧 BE（エンドポイント未実装の 404）や空応答を NONE として扱うための既定値。 */
const NONE_STATE: CheckoutRequestState = {
  status: 'NONE',
  paymentMethod: null,
  requestedAt: null,
  acknowledgedAt: null,
};

// 公開注文画面の会計リクエスト状態（ADR 0068）。
// 顧客は SSE を持たないため、初期表示時に GET し、REQUESTED / ACKNOWLEDGED の間だけ 10 秒間隔でポーリングする。
// タブ復帰（visibilitychange → visible）時も即時取得する。Redux には載せずローカル state で持つ。
// 取得失敗時は直前の状態を保持し、画面を壊さない。
export const useCheckoutRequestState = ({ activeStoreId, activeDiningTableId }: UseCheckoutRequestStateProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [state, setState] = useState<CheckoutRequestState | null>(null);
  // アンマウント後の setState を防ぐ
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!activeStoreId || !activeDiningTableId) return;
    try {
      const next = await dispatch(fetchCheckoutRequestState({ activeStoreId, activeDiningTableId })).unwrap();
      if (mountedRef.current) {
        setState(next ?? NONE_STATE);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      // 404（セッション非アクティブ / 旧 BE 未実装）は NONE 扱い。それ以外のエラーは直前の状態を保つ。
      if (error === CHECKOUT_REQUEST_SESSION_NOT_FOUND) {
        setState((prev) => prev ?? NONE_STATE);
      }
    }
  }, [dispatch, activeStoreId, activeDiningTableId]);

  // 初期取得
  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  // 状態がある間だけポーリング（NONE ではポーリングしない）
  const shouldPoll = state?.status === 'REQUESTED' || state?.status === 'ACKNOWLEDGED';
  useEffect(() => {
    if (!shouldPoll) return;
    const timer = window.setInterval(() => {
      refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [shouldPoll, refresh]);

  // タブ復帰時は即時取得（ポーリングがバックグラウンドで間引かれた分を取り戻す）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refresh]);

  return {
    state,
    /** PUT 応答（requestCheckout の CheckoutRequestState）を即時反映するために公開する */
    setState,
    refresh,
  };
};
